import { trustpilotTemplateLabel } from '~/composables/trustpilotFallbackVariant'

const OBSERVE_OPTIONS: MutationObserverInit = {
  childList: true,
  subtree: true,
  attributes: true,
  attributeFilter: ['title'],
}

/** Descriptive name for a widget, from the PIM's product name or the template. */
export function trustpilotFrameTitle(widget: HTMLElement): string {
  const name = widget.dataset.name?.trim()

  return name
    ? `Customer reviews for ${name}`
    : trustpilotTemplateLabel(widget.dataset.templateId)
}

function uniqueFrameTitle(base: string, frame: Element): string {
  const taken = new Set(
    Array.from(frame.ownerDocument.querySelectorAll('iframe[title]'))
      .filter(other => other !== frame)
      .map(other => other.getAttribute('title')),
  )

  if (!taken.has(base))
    return base

  let suffix = 2
  while (taken.has(`${base} ${suffix}`))
    suffix++

  return `${base} ${suffix}`
}

/**
 * Title every Trustpilot iframe below `root`, unique within the document.
 * Idempotent, and a no-op until the vendor bootstrap has built the iframe.
 */
export function applyTrustpilotFrameTitles(root: ParentNode): void {
  root.querySelectorAll<HTMLElement>('.trustpilot-widget').forEach((widget) => {
    const base = trustpilotFrameTitle(widget)

    widget.querySelectorAll('iframe').forEach((frame) => {
      const title = uniqueFrameTitle(base, frame)

      if (frame.getAttribute('title') !== title)
        frame.setAttribute('title', title)
    })
  })
}

/** Keeps the widget's iframe title unique as Trustpilot re-creates it. */
export function useTrustpilotFrameTitle(container: Ref<HTMLElement | null>): void {
  let observer: MutationObserver | undefined

  onMounted(() => {
    const root = container.value

    if (!root)
      return

    // loadFromElement discards the iframe and builds a new one with the stock
    // title, so re-apply on every mutation rather than once after mount.
    observer = new MutationObserver(() => {
      applyTrustpilotFrameTitles(root)
    })

    observer.observe(root, OBSERVE_OPTIONS)
    applyTrustpilotFrameTitles(root)
  })

  onBeforeUnmount(() => {
    observer?.disconnect()
    observer = undefined
  })
}
